import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { auth, googleProvider } from "../firebase";
import { signInWithPopup, signInWithRedirect, getRedirectResult } from "firebase/auth";

const isMobile = () => /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(navigator.userAgent);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export const hasPremiumPlan = (user) => {
  if (!user) return false;
  if (user.role === "superadmin") return true;
  const plano = (user.plano || "").toLowerCase();
  const status = user.plano_status || "";
  return (plano === "premium" || plano === "vitalicio") && status === "ativo";
};

export const isPlanActive = (user) => {
  if (!user) return false;
  if (user.role === "superadmin") return true;
  if (user.plano_status === "ativo") return true;
  const plano = user.plano || "";
  if (plano === "familia") return true;
  // New schema: plano="premium" or "basico" (any tipo)
  if (plano === "premium" || plano === "basico") return true;
  // Legacy schema
  if (plano === "anual" || plano === "vitalicio") return true;
  if (plano === "trial") {
    const exp = user.trial_expira_em;
    if (!exp) return false;
    try { return new Date(exp) >= new Date(); } catch { return false; }
  }
  return false;
};

export const formatApiError = (detail) => {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const userRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Token expirado/inválido — tenta silent re-auth via Firebase antes de deslogar
        // Isso resolve o caso do iOS PWA que limpa o localStorage após alguns dias
        try {
          const firebaseUser = auth.currentUser;
          if (firebaseUser) {
            const idToken = await firebaseUser.getIdToken(true);
            const { data } = await axios.post(`${API}/auth/firebase-login`, { id_token: idToken });
            if (data.token) localStorage.setItem("monyx_token", data.token);
            setUser(data.user);
            return;
          }
        } catch { /* silent re-auth falhou, segue para logout */ }
        setUser(false);
      } else {
        // Erro de rede / servidor (não é falha de auth) — desloga para não travar a tela
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      // Captura resultado do signInWithRedirect (mobile)
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          const idToken = await result.user.getIdToken();
          const { data } = await axios.post(`${API}/auth/firebase-login`, { id_token: idToken });
          if (data.token) localStorage.setItem("monyx_token", data.token);
          setUser(data.user);
          setLoading(false);
          window.location.replace("/app");
          return;
        }
      } catch { /* sem resultado de redirect, segue fluxo normal */ }
      checkAuth();
    };
    init();
  }, [checkAuth]); // eslint-disable-line

  // Keep ref in sync so interceptor can read current user without closure issues
  useEffect(() => { userRef.current = user; }, [user]);

  // Global axios interceptor — attaches token & detects 402 (trial expired)
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem("monyx_token");
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    const resId = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 402) {
          // Show paywall modal — never hard-redirect to /acesso-bloqueado automatically.
          // The /acesso-bloqueado page is only for direct navigation.
          setPaywallOpen(true);
        }
        return Promise.reject(err);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.token) localStorage.setItem("monyx_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    if (data.token) localStorage.setItem("monyx_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const googleLogin = async () => {
    // Mobile: usa redirect para evitar URL do Firebase aparecendo como popup/aba
    if (isMobile()) {
      await signInWithRedirect(auth, googleProvider);
      return; // Página vai redirecionar — resultado capturado por getRedirectResult no init
    }
    // Desktop: popup normal
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const { data } = await axios.post(`${API}/auth/firebase-login`, { id_token: idToken });
      if (data.token) localStorage.setItem("monyx_token", data.token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        console.error("Firebase Auth Error:", err);
        throw err;
      }
    }
  };

  const logout = async () => {
    const currentUserId = user?.user_id;
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    setUser(false);
    setPaywallOpen(false);
    localStorage.removeItem("monyx_token");
    // Clear per-user localStorage data
    if (currentUserId) {
      try { localStorage.removeItem(`monyx_app_data_${currentUserId}`); } catch { /* ignore */ }
    }
    // Also clear legacy key
    try { localStorage.removeItem("monyx_app_data_v1"); } catch { /* ignore */ }
  };

  const refreshUser = async () => {
    try { const { data } = await axios.get(`${API}/auth/me`); setUser(data); return data; } catch { return null; }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, googleLogin, logout, refreshUser, setUser, paywallOpen, setPaywallOpen }}>
      {children}
    </AuthCtx.Provider>
  );
};
